<template>
  <div>
    <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
      <PageHeader class="text-lg font-medium mr-auto" />
      <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
        <a class="button text-white bg-theme-1 shadow-md">
          {{ $t('text.create') }}
        </a>
      </div>
    </div>

    <div class="intro-y box px-5 my-5">
      <div class="nav-tabs flex flex-col sm:flex-row justify-center lg:justify-start">
        <a
          v-for="(tab, index) in tabs"
          :key="index"
          class="py-4 sm:mr-8"
          :class="{ active: currentTab === tab.key }"
          @click="onChangeTab(tab.key)"
        >
          {{ tab.label }}
        </a>
      </div>
    </div>

    <div class="intro-y mt-5 overflow-auto lg:overflow-visible">
      <CustomerTable
        :loading="isLoading"
        :rows="rows"
        :columns="columns"
        :line-numbers="true"
        :sort="sort"
        :page="page"
        :per-page="perPage"
        :page-size="pageSize"
        :total-page="totalPage"
        :total-records="totalRecords"
        :custom-filters="customFilters"
        :downloadable="true"
        @delete="confirmRemoval"
        @onFilter="onFilter"
        @onPageChange="onPageChange"
        @onPerPageChange="onPerPageChange"
        @onSortChange="onSortChange"
        @onAddContractDate="onAddContractDate"
        @onChangeCustomFilter="onChangeCustomFilter"
        @onDownload="onDownload"
      />
    </div>

    <ConfirmDialog :modal-id="deleteModalId" @ok="onDelete" />

    <CustomerAddContractDateModal
      :modal-id="contractDateModalId"
      :customer="customerToContractDate"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CUSTOMER_LIST_REQUEST,
  CUSTOMER_REMOVE,
  CUSTOMER_DOWNLOAD_REQUEST,
} from '@/store/actions/customer';
import PageHeader from '@/components/ui/PageHeader';
import CustomerTable from '@/components/customer/CustomerTable';
import CustomerAddContractDateModal from '@/components/customer/CustomerAddContractDateModal';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

export default {
  components: {
    PageHeader,
    CustomerTable,
    CustomerAddContractDateModal,
    ConfirmDialog,
  },

  data() {
    return {
      deleteModalId: 'delete-confirm-dialog',
      contractDateModalId: 'contract-date-modal',
      customerIdToRemove: null,
      customerIdToContractDate: null,
      rows: [],
      sort: {
        field: 'id',
        type: 'desc',
      },
      totalRecords: 0,
      page: 1,
      perPage: 20,
      pageSize: [10, 20, 30, 50, 100],
      totalPage: 1,
      filter: null,
      tabs: [
        {
          key: 1,
          label: this.$t('text.allCustomers'),
        },
        {
          key: 7,
          label: this.$t('text.new'),
        },
        {
          key: 2,
          label: this.$t('text.passed'),
        },
        {
          key: 4,
          label: this.$t('text.notPassed'),
        },
        {
          key: 3,
          label: this.$t('text.checkAgain'),
        },
        {
          key: 5,
          label: this.$t('text.blackList'),
        },
        {
          key: 6,
          label: this.$t('text.canGetLoan'),
        },
        {
          key: 8,
          label: this.$t('text.contracted'),
        },
        {
          key: 9,
          label: this.$t('text.contractHasExpired'),
        },
      ],
      currentTab: 1,
      tabFilters: [],
      customFilters: [
        {
          key: 'startDate',
          value: null,
          label: this.$t('text.selectStartDate'),
          type: 'day',
        },
        {
          key: 'endDate',
          value: null,
          label: this.$t('text.selectEndDate'),
          type: 'day',
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'customerList',
      'customerMeta',
      'customerListStatus',
      'getCustomerById',
      'customerDownloadStatus',
      'customerDownloadResponse',
    ]),
    isLoading() {
      return this.customerListStatus === 'loading';
    },
    columns() {
      return [
        {
          label: 'ID',
          field: 'id',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.score'),
          field: 'score',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.firstName'),
          field: 'firstName',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.registrationNumber'),
          field: 'registrationNumber',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.phone'),
          field: 'phone',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.createdAt'),
          field: 'createdAt',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.loanStatus'),
          field: 'status',
          sortable: true,
          filterable: true,
        },
        {
          label: this.$t('table.loansCount'),
          field: 'loansCount',
          sortable: true,
          disabled: this.currentTab !== 6,
        },
        {
          label: this.$t('table.contractDates'),
          field: 'contractDates',
          disabled: ![1, 8, 9, 6].includes(this.currentTab),
        },
        {
          label: this.$t('table.action'),
          field: 'action',
        },
      ];
    },
    customerToContractDate() {
      return this.customerIdToContractDate
        ? this.getCustomerById(this.customerIdToContractDate)
        : null;
    },
  },

  created() {
    this.fetchData();
  },

  methods: {
    confirmRemoval(id) {
      this.customerIdToRemove = id;
      cash(`#${this.deleteModalId}`).modal('show');
    },
    async onDelete() {
      cash(`#${this.deleteModalId}`).modal('hide');
      try {
        const payload = {
          businessId: this.business.id,
          id: this.customerIdToRemove,
        };
        await this.$store.dispatch(CUSTOMER_REMOVE, payload);
      } catch (error) {
        this.$toasted.show(error.message, { type: 'error' });
      }
      this.customerIdToRemove = null;
    },
    async onChangeTab(key) {
      this.currentTab = key;
      this.page = 1;

      if (key === 1) {
        this.tabFilters = [];
      } else if (key === 2) {
        this.tabFilters = [
          {
            field: 'score',
            value: 8,
          },
          {
            field: 'status',
            value: 'PENDING',
          },
        ];
      } else if (key === 3) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'CHECKED',
          },
        ];
      } else if (key === 4) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'NOT_MATCHED',
          },
        ];
      } else if (key === 5) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'BLACKLISTED',
          },
        ];
      } else if (key === 6) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'CONFIRMED',
          },
        ];
      } else if (key === 7) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'NEW',
          },
        ];
      } else if (key === 8) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'REQUESTED',
          },
        ];
      } else if (key === 9) {
        this.tabFilters = [
          {
            field: 'status',
            value: 'CONTRACT_EXPIRED',
          },
        ];
      }

      await this.fetchData();
    },
    onSortChange(params) {
      this.sort = params;
      this.onPageChange(1);
    },
    onPerPageChange(perPage) {
      this.perPage = perPage;
      this.onPageChange(1);
    },
    onPageChange(page) {
      this.page = page;
      this.fetchData();
    },
    onFilter(payload) {
      this.filter = payload;
      this.onPageChange(1);
    },
    onAddContractDate(id) {
      this.customerIdToContractDate = id;
      cash(`#${this.contractDateModalId}`).modal('show');
    },
    async fetchData() {
      const payload = {
        filters: {},
        sort: this.sort,
        page: this.page,
        perPage: this.perPage,
      };

      if (this.filter && this.filter.field) {
        payload.filters[`filter[${this.filter.field}]`] = this.filter.value;
      }

      if (this.tabFilters) {
        this.tabFilters.forEach(item => {
          payload.filters[`filter[${item.field}]`] = item.value;
        });
      }

      await this.$store.dispatch(CUSTOMER_LIST_REQUEST, payload);
      this.rows = this.customerList;
      this.totalRecords = this.customerMeta ? this.customerMeta.total : 1;
      this.page = this.customerMeta.currentPage;
      this.totalPage = this.customerMeta.lastPage;
    },
    onChangeCustomFilter(object) {
      const index = this.customFilters.findIndex(item => item.key === object.key);
      if (index > -1) {
        const { value } = object;
        this.customFilters[index].value = value || null;
      }
    },
    async onDownload() {
      const payload = this.customFilters.reduce((acc, cur) => {
        acc[`${cur.key}`] = cur.value;
        return acc;
      }, {});

      await this.$store.dispatch(CUSTOMER_DOWNLOAD_REQUEST, payload);
      this.setDownloads();
    },
    setDownloads() {
      if (this.customerDownloadStatus === 'success') {
        window.open(`${this.customerDownloadResponse}`, '_blank');
      }
    },
  },
};
</script>
